/**
 * Robust mobile detection for layout switching.
 *
 * Why not width-only:
 * - Android/iOS "Desktop site" often fakes viewport ~980–1200px → pure
 *   max-width:768 never triggers mobile shell.
 * - We combine: narrow viewport OR (touch + coarse pointer) OR mobile UA.
 */
import { useState, useEffect } from 'react';

const MOBILE_UA =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile/i;

function computeIsMobile(breakpoint = 768): boolean {
  if (typeof window === 'undefined') return false;

  const w = window.innerWidth;
  const narrow = w <= breakpoint;

  const coarse =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  const touchCapable =
    'ontouchstart' in window ||
    (navigator.maxTouchPoints != null && navigator.maxTouchPoints > 0);
  const tabletish = (coarse || touchCapable) && w <= 1024;

  const uaMobile = MOBILE_UA.test(navigator.userAgent || '');

  // Prefer real phone/tablet UA even when viewport is faked wide
  if (uaMobile && (touchCapable || coarse || w <= 1280)) return true;

  return narrow || tabletish;
}

type MqListener = (this: MediaQueryList, ev: MediaQueryListEvent) => void;

function mqOn(mq: MediaQueryList, handler: MqListener) {
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }
  // Safari < 14
  const legacy = mq as MediaQueryList & {
    addListener: (listener: MqListener) => void;
    removeListener: (listener: MqListener) => void;
  };
  legacy.addListener(handler);
  return () => legacy.removeListener(handler);
}

export function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(() => computeIsMobile(breakpoint));

  useEffect(() => {
    const update = () => setIsMobile(computeIsMobile(breakpoint));
    update();

    const mqWidth = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const mqCoarse = window.matchMedia('(pointer: coarse)');

    const offWidth = mqOn(mqWidth, update);
    const offCoarse = mqOn(mqCoarse, update);

    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    return () => {
      offWidth();
      offCoarse();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [breakpoint]);

  return isMobile;
}
